import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { inr, ddmmyyyy, LR_STATUSES } from "../lib/format";
import { GRNItemCard } from "../components/grn/GRNItemCard";
import { NameList, useMaster } from "../components/setup/PartiesTab";

const HEAD = ["grn_date", "dest_type", "receiving_location", "fabricator_id", "invoice_no", "invoice_date", "lr_no", "lr_date", "transporter", "lr_status", "remarks"];

const F = ({ label, children }) => (
  <div className="min-w-0">
    <label className="label">{label}</label>
    {children}
  </div>
);

export default function GRNForm() {
  const { poId, grnId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [transporters] = useMaster("transporters");
  useEffect(() => {
    const load = async () => {
      const grn = grnId ? (await api.get(`/grns/${grnId}`)).data : null;
      const pid = grn ? grn.po_id : poId;
      const { data: t } = await api.get("/grns/template", { params: { po_id: pid, grn_id: grnId || undefined } });
      if (!grn && t.po.status !== "Accepted") {
        toast.error("GRN can be raised only against an Accepted PO", { id: "grn-guard" });
        navigate("/pos");
        return;
      }
      const qty = Object.fromEntries((grn?.items || []).map((i) => [i.po_item_id, i]));
      const pants = t.po.product_line?.startsWith("Pants");
      const [locs, fabs] = (await Promise.all([api.get("/locations"), api.get("/fabricators")])).map((r) => r.data);
      const kindOf = (name) => locs.find((l) => l.name === name)?.kind === "Showroom" ? "Showroom" : "Godown";
      const form = grn ? { ...Object.fromEntries(HEAD.map((k) => [k, grn[k] || ""])),
        dest_type: grn.dest_type || (grn.fabricator_id ? "Fabricator" : kindOf(grn.receiving_location)) } : {
        grn_date: t.today, dest_type: pants ? "Godown" : "Fabricator", receiving_location: "", fabricator_id: "", invoice_no: "", invoice_date: "", lr_no: "",
        lr_date: "", transporter: "", lr_status: "Booked", remarks: "" };
      const items = t.items.map((it) => ({ ...it, qty_invoice: qty[it.po_item_id]?.qty_invoice ?? "", qty_received: qty[it.po_item_id]?.qty_received ?? "" }));
      setData({ po: t.po, grn, form, items, pants, locs, fabs });
    };
    load().catch((e) => toast.error(errMsg(e)));
  }, [poId, grnId, navigate]);

  const totals = useMemo(() => (data?.items || []).reduce((a, it) => {
    const v = (Number(it.qty_received) || 0) * it.rate;
    return { value: a.value + v, tax: a.tax + (v * it.gst) / 100 };
  }, { value: 0, tax: 0 }), [data]);

  if (!data) return <p className="text-muted-foreground" data-testid="grn-form-loading">Loading…</p>;
  const { po, grn, form, items, pants, locs, fabs } = data;
  const set = (k) => (e) => setData({ ...data, form: { ...form, [k]: e.target.value } });
  const setItem = (id, patch) => setData((d) => ({ ...d, items: d.items.map((it) => (it.po_item_id === id ? { ...it, ...patch } : it)) }));
  const input = (k, type = "text") => <input type={type} className="field" value={form[k]} onChange={set(k)} data-testid={`grn-${k.replace(/_/g, "-")}-input`} />;

  const save = async () => {
    if (form.dest_type === "Fabricator" ? !form.fabricator_id : !form.receiving_location) return toast.error(`Pick the ${form.dest_type.toLowerCase()} receiving the goods`);
    if (!items.some((it) => Number(it.qty_invoice) > 0 || Number(it.qty_received) > 0)) {
      return toast.error("Enter invoice or received quantity for at least one item");
    }
    setSaving(true);
    const body = { ...form, receiving_location: form.dest_type === "Fabricator" ? "" : form.receiving_location,
      fabricator_id: form.dest_type === "Fabricator" ? form.fabricator_id : "", po_id: po.id, invoice_date: form.invoice_date || null, lr_date: form.lr_date || null,
      items: items.map((it) => ({ po_item_id: it.po_item_id, qty_invoice: Number(it.qty_invoice) || 0, qty_received: Number(it.qty_received) || 0 })) };
    try {
      const { data: g } = grn ? await api.put(`/grns/${grn.id}`, body) : await api.post("/grns", body);
      toast.success(`${g.grn_no} saved`);
      navigate("/grns", { state: { open: g.id } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="grn-form">
      <div>
        <p className="mono-label text-brand">{grn ? `Editing ${grn.grn_no}` : "Goods receipt note"} · {po.po_no}</p>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{grn ? "Edit GRN" : "New GRN"}</h1>
        <p className="text-sm text-muted-foreground">{po.vendor.name} ({po.vendor.code}) · PO date {ddmmyyyy(po.po_date)}{pants ? " · Pants go straight into stock at the receiving location" : ""}</p>
      </div>
      <section className="panel rise space-y-4 p-4" data-testid="grn-receipt-section">
        <h2 className="mono-label text-muted-foreground">Receipt</h2>
        <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2">
          <F label="GRN date *">{input("grn_date", "date")}</F>
          <F label="Delivered to *">
            <select className="field" value={form.dest_type} onChange={(e) => setData({ ...data, form: { ...form, dest_type: e.target.value, receiving_location: "", fabricator_id: "" } })} data-testid="grn-dest-type-select">
              {(pants ? ["Godown", "Showroom"] : ["Fabricator", "Godown", "Showroom"]).map((d) => <option key={d}>{d}</option>)}
            </select>
          </F>
          <F label={`Which ${form.dest_type.toLowerCase()} *`}>{form.dest_type === "Fabricator" ? (
            <select className="field" value={form.fabricator_id} onChange={set("fabricator_id")} data-testid="grn-factory-select">
              <option value="">Select…</option>
              {fabs.filter((f) => f.active !== false).map((f) => <option key={f.id} value={f.id}>{f.name} ({f.code})</option>)}
            </select>
          ) : (
            <select className="field" value={form.receiving_location} onChange={set("receiving_location")} data-testid="grn-receiving-location-select">
              <option value="">Select…</option>
              {locs.filter((l) => (form.dest_type === "Showroom" ? l.kind === "Showroom" : l.kind !== "Showroom")).map((l) => <option key={l.id} value={l.name}>{l.name}</option>)}
            </select>
          )}</F>
          <F label="Vendor invoice no">{input("invoice_no")}</F>
          <F label="Invoice date">{input("invoice_date", "date")}</F>
        </div>
      </section>
      <section className="panel rise space-y-4 p-4" data-testid="grn-lr-section">
        <h2 className="mono-label text-muted-foreground">Transport (LR)</h2>
        <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2">
          <F label="LR number">{input("lr_no")}</F>
          <F label="LR date">{input("lr_date", "date")}</F>
          <F label="Transporter"><input className="field" list="grn-transporters" value={form.transporter} onChange={set("transporter")} placeholder="Pick or type" data-testid="grn-transporter-input" /><NameList id="grn-transporters" list={transporters} /></F>
          <F label="LR status">
            <select className="field" value={form.lr_status} onChange={set("lr_status")} data-testid="grn-lr-status-select">
              {LR_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </F>
        </div>
      </section>
      <div className="space-y-3" data-testid="grn-items-section">
        <h2 className="mono-label px-1 text-muted-foreground">Items ({items.length})</h2>
        {items.map((it, i) => <GRNItemCard key={it.po_item_id} item={it} index={i} onChange={(p) => setItem(it.po_item_id, p)} />)}
      </div>
      <section className="panel p-4">
        <label className="label">Remarks</label>
        <textarea className="field" rows={2} value={form.remarks} onChange={set("remarks")} data-testid="grn-remarks-input" />
      </section>
      <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t bg-card/95 backdrop-blur-lg" data-testid="grn-totals-bar">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="mono truncate text-xs text-muted-foreground" data-testid="grn-totals-value-tax">Received {inr(totals.value)} + GST {inr(totals.tax)}</p>
            <p className="mono truncate text-xl font-bold" data-testid="grn-totals-total">{inr(totals.value + totals.tax)}</p>
          </div>
          <button className="btn btn-accent shrink-0" onClick={save} disabled={saving} data-testid="save-grn-btn">
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} {grn ? "Update GRN" : "Save GRN"}
          </button>
        </div>
      </div>
    </div>
  );
}
